
import React from 'react';
import { CheckIcon } from './icons/CheckIcon';
import { ShieldIcon } from './icons/ShieldIcon';
import { ClockIcon } from './icons/ClockIcon';
import { UsersIcon } from './icons/UsersIcon';
import { CheckCircleIcon } from './icons/CheckCircleIcon';
import { SparkleIcon } from './icons/SparkleIcon';
import { ArrowRightIcon } from './icons/ArrowRightIcon';
import { TargetIcon } from './icons/TargetIcon';
import { BookOpenIcon } from './icons/BookOpenIcon';
import { XCircleIcon } from './icons/XCircleIcon';


interface WhyForgivePathPageProps {
  onStartQuiz: () => void;
}

// --- Data ---

const DollarSignIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        fill="none" 
        viewBox="0 0 24 24" 
        strokeWidth={2}
        stroke="currentColor" 
        {...props}
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33" />
    </svg>
);

const plans = [
    {
        name: 'Free Assessment', price: 0, description: 'Perfect for getting started',
        features: ['Eligibility quiz', 'Estimated savings calculator', 'Program recommendations', 'Basic resource access', 'Digital assistant (limited)'],
        popular: false, buttonText: 'Start Free Assessment',
    },
    {
        name: 'Application Support', price: 79, description: 'Complete application assistance',
        features: ['Everything in Free', 'Full application preparation', 'Document validation & guidance', 'Form generation & submission', 'Unlimited digital assistant access', 'Priority processing', 'Progress tracking dashboard'],
        popular: true, buttonText: 'Get Full Support',
    }
];

const trustBadges = [
    { icon: <ShieldIcon className="w-8 h-8 text-blue-500" />, title: 'Money-Back Guarantee', description: "If your application is denied by the government, simply upload your official denial letter in your account settings for a full refund." },
    { icon: <ClockIcon className="w-8 h-8 text-green-500" />, title: 'No Hidden Fees', description: 'One-time payment, no recurring charges or surprises' },
    { icon: <UsersIcon className="w-8 h-8 text-blue-600" />, title: 'High Accuracy', description: "Our system ensures applications are complete and validated against program rules prior to submission." },
];

const coreValues = [
    { icon: <UsersIcon className="w-8 h-8 text-blue-500" />, title: "Empowerment", description: "As a woman-owned, solo-founded business, ForgivePath believes in leveling the playing field for all borrowers." },
    { icon: <TargetIcon className="w-8 h-8 text-green-500" />, title: "Precision", description: "Every application is processed using rigorous automated validation logic—because accuracy prevents delays and changes lives." },
    { icon: <BookOpenIcon className="w-8 h-8 text-blue-600" />, title: "Transparency", description: "Honest pricing, clear answers, and no surprises. We are a private company helping you navigate the system." },
    { icon: <UsersIcon className="w-8 h-8 text-green-600" />, title: "Simplicity", description: "We know you are busy. We built this platform to be fast, intuitive, and stress-free, stripping away the complexity of government forms." },
];

const comparisonData = [
    { feature: 'Cost', forgivePath: '$79 (one-time)', diy: 'Free (but costs time & risk)', consultants: '$300 - $1,000+', lawyers: '$1,000 - $5,000+' },
    { feature: 'Identifies Eligible Programs', forgivePath: true, diy: false, consultants: true, lawyers: true },
    { feature: 'Time to Complete', forgivePath: '< 30 minutes', diy: '10-20+ hours', consultants: 'Several hours of calls/emails', lawyers: 'Several hours of calls/emails' },
    { feature: 'Automated Document Prep', forgivePath: true, diy: false, consultants: true, lawyers: true },
    { feature: 'Handles All Paperwork', forgivePath: true, diy: false, consultants: true, lawyers: true },
    { feature: '24/7 Digital Support', forgivePath: true, diy: false, consultants: false, lawyers: false },
    { feature: 'Founded by Tech Expert', forgivePath: true, diy: false, consultants: false, lawyers: false },
];

// --- Sub-Components ---

const PricingCard: React.FC<{ plan: typeof plans[0]; onSelectPlan: () => void; }> = ({ plan, onSelectPlan }) => (
    <div className={`bg-white p-8 rounded-2xl shadow-sm border ${plan.popular ? 'border-2 border-blue-500' : 'border-gray-200'} flex flex-col relative`}>
        {plan.popular && <div className="absolute top-0 -translate-y-1/2 w-full flex justify-center"><span className="bg-green-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase">Most Popular</span></div>}
        <h3 className="text-xl font-bold text-gray-900 text-center">{plan.name}</h3>
        <div className="my-6 text-center">
            <span className="text-5xl font-extrabold bg-gradient-to-r from-green-500 to-blue-600 bg-clip-text text-transparent">${plan.price}</span>
            {plan.price > 0 && <span className="text-gray-500 font-medium ml-1">one-time</span>}
        </div>
        <p className="text-gray-600 text-center mb-8 h-12">{plan.description}</p>
        <ul className="space-y-4 text-gray-700 mb-8 flex-grow">
            {plan.features.map(feat => (
                <li key={feat} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0"><CheckIcon className="w-3.5 h-3.5 text-green-600" /></div>
                    <span>{feat}</span>
                </li>
            ))}
        </ul>
        <button onClick={onSelectPlan} className={`w-full text-lg font-bold py-3 px-6 rounded-full transition-all duration-300 mt-auto shadow-lg ${plan.price === 0 ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
            {plan.buttonText}
        </button>
    </div>
);

const FeatureCell: React.FC<{ value: string | boolean }> = ({ value }) => {
    if (typeof value === 'boolean') {
        return value ? <CheckCircleIcon className="w-6 h-6 text-green-500 mx-auto" /> : <XCircleIcon className="w-6 h-6 text-gray-400 mx-auto" />;
    }
    return <span className="text-gray-700">{value}</span>;
};


// --- Main Page Component ---

const WhyForgivePathPage: React.FC<WhyForgivePathPageProps> = ({ onStartQuiz }) => {
  return (
    <div className="space-y-16 sm:space-y-24">
      {/* --- Section 1: Pricing --- */}
      <section>
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-400 to-blue-500 rounded-full mb-6"><DollarSignIcon className="w-8 h-8 text-white" /></div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">Simple, Transparent Pricing</h1>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">Check your eligibility for free. Get full application support for a one-time fee of $79—no hidden charges, ever.</p>
          </div>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {plans.map(plan => <PricingCard key={plan.name} plan={plan} onSelectPlan={onStartQuiz} />)}
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center max-w-5xl mx-auto">
            {trustBadges.map(badge => (
                <div key={badge.title} className="bg-gray-50 p-6 rounded-2xl border border-gray-100 flex flex-col items-center">
                    <div className="bg-white p-3 rounded-xl mb-4 shadow-sm">{badge.icon}</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-1">{badge.title}</h4>
                    <p className="text-gray-600">{badge.description}</p>
                </div>
            ))}
          </div>
      </section>

      {/* --- Section 2: About Us --- */}
      <section>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">Built for Your Success, Powered by Real Experience</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              ForgivePath was founded and is run entirely by a veteran Wall Street financial technology expert specializing in finance, Excel, APIs, and modern automation. Our platform is built on powerful, reliable logic, bulletproof compliance, and genuine respect for your time and privacy.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
              {coreValues.map((value) => (
                  <div key={value.title} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start text-left h-full">
                      <div className="bg-gray-100 p-3 rounded-xl mb-6">{value.icon}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                      <p className="text-gray-600">{value.description}</p>
                  </div>
              ))}
          </div>
      </section>

      {/* --- Section 3: Compare Us --- */}
      <section>
           <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">The Smartest Path to Forgiveness</h2>
                <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">See how ForgivePath stacks up against other options. We combine expert-level accuracy with powerful automation at an unbeatable price.</p>
            </div>
            <div className="overflow-x-auto max-w-6xl mx-auto">
                <div className="min-w-[800px]">
                    <div className="grid grid-cols-5 gap-4 text-center font-bold text-gray-800 p-4 bg-gray-50 rounded-t-lg">
                        <div className="text-left">Feature</div>
                        <div className="text-blue-600">ForgivePath</div>
                        <div>DIY (Do-It-Yourself)</div>
                        <div>Loan Consultants</div>
                        <div>Lawyers</div>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {comparisonData.map((row, index) => (
                            <div key={index} className="grid grid-cols-5 gap-4 items-center p-4 text-center bg-white">
                                <div className="font-semibold text-gray-800 text-left">{row.feature}</div>
                                <div className="font-bold text-blue-600"><FeatureCell value={row.forgivePath} /></div>
                                <div><FeatureCell value={row.diy} /></div>
                                <div><FeatureCell value={row.consultants} /></div>
                                <div><FeatureCell value={row.lawyers} /></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
      </section>

      {/* --- Final CTA --- */}
      <section>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                <div className="bg-gradient-to-r from-[#2ECC40] to-[#0074D9] rounded-2xl shadow-xl p-12 text-center text-white">
                    <SparkleIcon className="w-12 h-12 mx-auto mb-4" />
                    <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">Ready to start your success story?</h2>
                    <p className="mt-4 max-w-2xl mx-auto text-lg md:text-xl text-green-100">Get your free assessment today and discover what a solo, woman-led, Wall Street-built platform can do for you.</p>
                    <div className="mt-10">
                        <button onClick={onStartQuiz} className="bg-white text-[#0074D9] font-bold text-lg py-4 px-8 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto">
                            Get Your Free Assessment
                            <ArrowRightIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    </div>
  );
};

export default WhyForgivePathPage;
